<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject } from "vue"
import { useI18n } from "vue-i18n"
import { useDebounceFn } from "@vueuse/core"
import { createUniqueId } from "@core/helpers"
import { useAuthStore } from "@core/stores/auth.store"
import { useTaskStore } from "@core/stores/task.store"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import type { Services } from "@services"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Liste des sources",
            period_label: "Période de facturation de l'abonnement",
            period_dates: "{start} - {end}",
            export_button: "Exporter les données",
            export_label: "Export des sources de facturation",
        },
    },
})

// ================================
// SERVICES & STORES
// ================================
const services = inject<Services>("services")!
const authStore = useAuthStore()
const taskStore = useTaskStore()

// ================================
// STATE
// ================================

/**
 * Contrôle la visibilité du dialog
 */
const [visible] = defineModel<boolean>("visible", {
    required: true,
    default: false,
})

/**
 * Offre courante (période de facturation) depuis le store auth
 */
const getCurrentOffer = computed(() => authStore.getCurrentOffer ?? null)

/**
 * Formate une date de période en français ("31 mai") via `Intl`.
 * On n'utilise PAS `formatDate(..., "fr")` (moment) ici : les fichiers de locale
 * moment ne se bundlent pas de façon fiable en prod (instance moment-timezone +
 * résolution `moment/locale/fr` absente côté Heroku) → les mois retombaient en
 * anglais. `Intl` est natif au runtime, sans dépendance ni locale à charger.
 */
const periodDateFormatter = new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    timeZone: "Europe/Paris",
})
function formatPeriodDate(
    value: string | number | Date | null | undefined
): string {
    if (value === null || value === undefined || value === "") return "-"
    const date = new Date(value)
    return Number.isNaN(date.getTime())
        ? "-"
        : periodDateFormatter.format(date)
}

/**
 * Wrapper d'appel API pour l'export des sources de facturation (loading, erreur, annulation)
 */
const {
    fetch: fetchExport,
    isFetching: isExporting,
    hasError: hasExportError,
    error: exportError,
} = useFetchWrapper(
    async (_kwargs, _controller) => {
        const response = await services.$downloadExportSourceBilling()
        if (response.status === "success" && response.result?.url) {
            window.open(response.result.url, "_blank")
        }
        const taskId = createUniqueId()
        taskStore.followTask(taskId, {
            taskname: t("export_label"),
            notification: true,
            autoDelete: true,
        })
    },
    (err) => {
        console.error("Error exporting billing sources:", err)
    }
)

/**
 * Déclenche l'export (débounced)
 */
const onExportClick = useDebounceFn(() => {
    fetchExport()
}, 1000)
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        modal
        position="top"
        :closable="true"
        dismissable-mask
        class="company-billing-source-dialog"
        :style="{ width: '90vw' }"
        :header="t('title')"
        :pt="{
            content: 'overflow-hidden!',
        }"
    >
        <template #header>
            <h2 class="text-lg text-center mx-auto font-semibold text-primary">
                {{ t("title") }}
            </h2>
        </template>

        <div
            class="company-billing-source-dialog__content flex flex-col max-h-[85vh] overflow-hidden py-4"
        >
            <!-- Période + bouton Exporter -->
            <div
                class="company-billing-source-dialog__header flex flex-col items-center gap-4 pb-4"
            >
                <div
                    v-if="getCurrentOffer"
                    class="flex flex-col items-center gap-1 text-gray-600"
                >
                    <span>{{ t("period_label") }}</span>
                    <span>
                        {{
                            t("period_dates", {
                                start: formatPeriodDate(
                                    getCurrentOffer.current_start_period_monthly
                                ),
                                end: formatPeriodDate(
                                    getCurrentOffer.current_end_period_monthly
                                ),
                            })
                        }}
                    </span>
                </div>
                <BaseButton
                    :label="t('export_button')"
                    severity="secondary"
                    :loading="isExporting"
                    :disabled="isExporting"
                    @click="onExportClick"
                />
                <p v-if="hasExportError" class="text-sm text-red-600">
                    {{ exportError?.message }}
                </p>
            </div>
            <CompanyBillingSourceList embedded />
        </div>
    </BaseDialog>
</template>
